import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { startCase } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import { StatusTabFilters } from '~components/StatusTabFilters/StatusTabFilters';
import { Company } from '~hooks/useCompany/models';
import useManagedCompanies from '~hooks/useManagedCompanies/useManagedCompanies';
import { ROUTES } from '~router/routePaths';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { ManagedVendorDriversTab } from './ManagedVendorDriversTab';
import { ManagedVendorEquipmentTab } from './ManagedVendorEquipmentTab';
import { ManagedVendorUsersTab } from './ManagedVendorUsersTab';

enum TabValue {
  DRIVERS = 'drivers',
  EQUIPMENT = 'equipment',
  USERS = 'users',
}

export const VendorManagement = observer(() => {
  const { managedCompanyId } = useParams<{ managedCompanyId: string }>();
  const { getManagedCompanyById, isLoading } = useManagedCompanies();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [managedCompany, setManagedCompany] = useState<Company | null>(null);
  const [activeTab, setActiveTab] = useState<TabValue>(TabValue.DRIVERS);

  useEffect(() => {
    if (managedCompanyId && companyId) {
      getManagedCompanyById({ companyId, managedCompanyId }).then(setManagedCompany);
    }
  }, [managedCompanyId, companyId]);

  if (isLoading || !managedCompany) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const formatAddress = () => {
    if (!managedCompany.address) return '';
    const { streetAddress, premise, country, administrativeArea } =
      managedCompany.address;
    return `${streetAddress}${premise ? ` ${premise},` : ','} ${administrativeArea}${country?.code ? `, ${country.code}` : ''}`;
  };

  const tabs = [
    {
      value: TabValue.DRIVERS,
      label: t('administration.managed_companies_v2.tabs.drivers'),
    },
    {
      value: TabValue.EQUIPMENT,
      label: t('administration.managed_companies_v2.tabs.equipment'),
    },
    {
      value: TabValue.USERS,
      label: t('administration.managed_companies_v2.tabs.users'),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <PseudoLink
          title={`<- ${t('common.vendors')}`}
          to={`/${ROUTES.settings.base}/${ROUTES.settings.vendors}`}
        />
      </Box>
      <Box sx={{ flex: 1, mb: 1 }}>
        <Typography variant="page-title" sx={{ mb: 3 }}>
          {managedCompany.legalName}
        </Typography>

        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            {managedCompany.companyType && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {startCase(managedCompany.companyType)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  •
                </Typography>
              </>
            )}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('administration.managed_companies_v2.tread_id')} {managedCompany.treadId}
            </Typography>
          </Box>

          {managedCompany.address && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {formatAddress()}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {managedCompany.primaryContact?.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {managedCompany.primaryContact.phone}
                </Typography>
              </Box>
            )}

            {managedCompany.primaryContact?.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {managedCompany.primaryContact.email}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 4 }}>
        <StatusTabFilters value={activeTab} onChange={setActiveTab} filters={tabs} />
      </Box>
      <Box sx={{ mt: 3 }}>
        {activeTab === TabValue.DRIVERS && (
          <ManagedVendorDriversTab
            managedCompanyId={managedCompanyId!}
            managedCompany={managedCompany}
          />
        )}
        {activeTab === TabValue.EQUIPMENT && (
          <ManagedVendorEquipmentTab
            managedCompanyId={managedCompanyId!}
            managedCompany={managedCompany}
          />
        )}
        {activeTab === TabValue.USERS && (
          <ManagedVendorUsersTab
            managedCompanyId={managedCompanyId!}
            managedCompany={managedCompany}
          />
        )}
      </Box>
    </Box>
  );
});
